import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NavigationEnd, Router } from '@angular/router';
import { MainTreeMenuData, SideMenuNode } from '@bo-client/core/navigation/main-menu.definition';
import { MenuModel } from '@bo-client/core/navigation/menu.model';
import { NavigationService } from '@bo-client/core/navigation/navigation.service';
import { TenantDto } from '@bo-client/shared/models/tenantDto';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { combineLatestWith, filter, map, startWith, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss']
})
export class SideNavComponent implements OnInit, OnDestroy {
  @Output() menuExpanded = new BehaviorSubject<boolean>(false);

  public treeControl = new NestedTreeControl<SideMenuNode>((node) => node.children);
  public dataSource = new MatTreeNestedDataSource<SideMenuNode>();
  public currentUrl = '';
  private compUnsubs$ = new Subject<void>();

  // Neue Properties für das Autocomplete
  tenantFilterControl = new FormControl();
  filteredTenants: Observable<TenantDto[]>;

  constructor(
    private navigationService: NavigationService,
    public dataShareService: DataShareService,
    private router: Router
  ) {
    const menuState = localStorage.getItem('menu_expanded');
    this.menuExpanded = new BehaviorSubject(menuState === null || menuState === 'true');

    this.router.events.pipe(takeUntil(this.compUnsubs$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = decodeURI(event.urlAfterRedirects.substr(1));
      }
    });
  }

  ngOnInit() {
    this.dataShareService.userData$
      .pipe(
        takeUntil(this.compUnsubs$),
        combineLatestWith(this.dataShareService.tenantConfig$, this.dataShareService.uiAdaptions$),
        filter(([userData, tenantConfig, uiAdaptions]) => !!userData && !!tenantConfig && !!uiAdaptions)
      )
      .subscribe(() => {
        this.dataSource.data = this.navigationService.generateTreeMenu(MainTreeMenuData);
      });

    // Initialisierung des aktuellen Tenants im FormControl
    this.dataShareService.currentTenant$.pipe(takeUntil(this.compUnsubs$)).subscribe((tenant) => {
      if (tenant) {
        this.tenantFilterControl.setValue(tenant);
      }
    });

    // Filterlogik für das Autocomplete
    this.filteredTenants = this.tenantFilterControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
        return this.filterTenants(filterValue);
      })
    );
  }

  // Filterfunktion für Tenants
  private filterTenants(value: string): TenantDto[] {
    const tenants = this.dataShareService.availableTenants;
    if (!value) {
      return tenants;
    }

    return tenants.filter((tenant) => tenant.displayName.toLowerCase().includes(value));
  }

  // Display-Funktion für das Autocomplete
  displayTenant(tenant: TenantDto): string {
    return tenant ? tenant.displayName : '';
  }

  hasChild = (_: number, node: SideMenuNode) => !!node.children && node.children.length > 0;

  setTenant(tenant: TenantDto) {
    this.dataShareService.setTenant(tenant);
  }

  toggleMenu() {
    const newValue = !this.menuExpanded.getValue();
    this.menuExpanded.next(newValue);
    localStorage.setItem('menu_expanded', newValue ? 'true' : 'false');
  }

  getPosition(string, subString, index) {
    return string.split(subString, index).join(subString).length;
  }

  isSelected(node: MenuModel) {
    if (window.innerWidth < 1024 && localStorage.getItem('menu_expanded') === 'true') {
      this.toggleMenu();
    }
    this.router.navigate([node.link]);
  }

  mouseEnterNode(event: MouseEvent) {
    const menuItem = event.target as HTMLInputElement;

    // If it's a base Node first hide all submenu groups
    if (menuItem.getAttribute('aria-level') === '1') {
      const expandables = document.querySelectorAll('.nav__group');
      if (expandables) {
        expandables.forEach((node) => {
          node.classList.remove('nav__group--show');
        });
      }
    }

    // If it's got a submenu show it
    const submenuWrapper = menuItem.querySelector('.nav__group') as HTMLElement;
    if (submenuWrapper) {
      // How tall is the header
      const header = document.querySelector('.shell__header') as HTMLElement;
      const headerHeight = header.offsetHeight;

      // Where is menu item in relation to the viewport
      const menuItemOffset = menuItem.getBoundingClientRect().top;

      // How tall is the submenu
      const groupHeight = submenuWrapper.offsetHeight;

      // How much space is there to the bottom of the viewport
      const menuItemSpaceToBottom = window.innerHeight - menuItem.getBoundingClientRect().top;

      // Place the submenu in the correct position relevant to the menu item
      // Never let it get negative to avoid the submenu being cut off at the top if it's bigger than the screen
      // If there's enough space move the submenu up, so it barely fits inside the viewport
      submenuWrapper.style.top =
        Math.max(0, menuItemOffset - headerHeight - Math.max(0, groupHeight - menuItemSpaceToBottom + 7)) +
        'px';
      submenuWrapper.classList.add('nav__group--show');
    }
  }

  mouseEnterGroup(event: MouseEvent) {
    const group = event.target as HTMLElement;
    group.classList.add('nav__group--show');
  }

  mouseLeaveGroup(event: MouseEvent) {
    const group = event.target as HTMLElement;
    group.classList.remove('nav__group--show');
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }

  protected readonly encodeURI = encodeURI;
}
